import { DndContext } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { memo, useCallback, useMemo, useState } from "react";

import type { PlainWidgetItemInput, SnackListForDesignPlainQuery } from "@/lib/gql/__generated__/graphql";

import { Button } from "@/components/ui/button/button";

import { AddSnackDialog } from "./dialog";
import { SnackCard } from "./snack";

import type { DragEndEvent } from "@dnd-kit/core";

type Props = {
	hotelID: string;
	snacks: SnackListForDesignPlainQuery["snackList"]["snacks"];
	plainWidgetItems: Array<PlainWidgetItemInput>;

	onAddPlainWidget: (contentID: string, title: string) => void;
	onRemovePlainWidget: (widgetID: string) => void;
	// Widgetsの並び替え内容を反映する
	onUpdateOrderPlainWidgets: (widgetIDs: Array<string>) => void;
};

export const AddSnackWithDialog = memo(
	({
		hotelID,
		snacks,
		plainWidgetItems,
		onAddPlainWidget,
		onRemovePlainWidget,
		onUpdateOrderPlainWidgets,
	}: Props) => {
		const [canShowDialog, setCanShowDialog] = useState(false);
		const handleOpenDialog = useCallback(() => {
			setCanShowDialog(true);
		}, []);
		const handleChangeDialog = useCallback((value: boolean) => {
			setCanShowDialog(value);
		}, []);

		const handleDragEnd = useCallback(
			({ active, over }: DragEndEvent) => {
				if (active && over) {
					const activeIndex = plainWidgetItems.findIndex((item) => item.widgetID === active.id);
					const overIndex = plainWidgetItems.findIndex((item) => item.widgetID === over.id);
					const newPlainWidgetItems = [...plainWidgetItems];
					const [removed] = newPlainWidgetItems.splice(activeIndex, 1);
					newPlainWidgetItems.splice(overIndex, 0, removed);

					onUpdateOrderPlainWidgets(newPlainWidgetItems.map((item) => item.widgetID));
				}
			},
			[onUpdateOrderPlainWidgets, plainWidgetItems],
		);

		const itemsForSortableContext = useMemo(() => {
			return plainWidgetItems.map((item) => {
				return { id: item.widgetID };
			});
		}, [plainWidgetItems]);

		return (
			<>
				<div className="space-y-3 px-2 py-3">
					<div className="flex justify-between">
						<label>スナックの追加</label>
						<Button variant="outline" type="button" onClick={handleOpenDialog}>
							スナックを追加する
						</Button>
					</div>

					<DndContext onDragEnd={handleDragEnd}>
						<div className="mx-auto flex flex-wrap gap-4">
							{plainWidgetItems.map((widget) => {
								const snack = snacks.find((s) => s.id === widget.contentID);
								return snack ? (
									<SortableContext key={widget.widgetID} items={itemsForSortableContext}>
										<div key={widget.widgetID} className="min-w-72 max-w-72">
											<SnackCard
												widgetID={widget.widgetID}
												title={snack?.title ?? ""}
												thumbnail={snack?.thumbnail ?? ""}
												onRemoveSnack={() => onRemovePlainWidget(widget.widgetID)}
											/>
										</div>
									</SortableContext>
								) : null;
							})}
						</div>
					</DndContext>
				</div>

				<AddSnackDialog
					hotelID={hotelID}
					snacks={snacks}
					open={canShowDialog}
					onAddPlainWidget={onAddPlainWidget}
					onOpenChange={handleChangeDialog}
				/>
			</>
		);
	},
);
